<template>
  <div class="stations-page">
    <v-card class="stations-card rounded-xl pa-4 d-flex flex-column" variant="flat" :loading="loading">
      <div class="toolbar-top mb-4 flex-shrink-0">
        <div class="toolbar-main d-flex flex-wrap align-center ga-4">
          <div class="search-item">
            <span class="search-label">区域</span>
            <v-select v-model="queryProvince" :items="provinceItems" item-title="title" item-value="value"
              placeholder="全部区域" variant="outlined" density="compact" hide-details clearable
              class="search-input--select" rounded="xl" />
          </div>
          <div class="search-item">
            <span class="search-label">eBlock型号</span>
            <v-select v-model="queryEBlockModel" :items="eBlockModelFilterItems" item-title="title" item-value="value"
              placeholder="全部型号" variant="outlined" density="compact" hide-details clearable
              class="search-input--select" rounded="xl" />
          </div>
          <div class="search-item">
            <span class="search-label">关键字</span>
            <v-text-field v-model="searchInput" placeholder="按名称/简称/电站ID" variant="outlined" density="compact"
              hide-details clearable class="search-input" rounded="xl" @keyup.enter="handleSearch" />
          </div>

          <div class="search-actions d-flex ga-3">
            <v-btn :variant="hasAdvancedFilters ? 'flat' : 'outlined'"
              :color="hasAdvancedFilters ? '#2655f1' : '#2655f1'"
              :class="['action-btn', { 'action-btn--active': hasAdvancedFilters }]" rounded="xl"
              @click="openAdvancedFilterDialog">
              <v-icon start icon="mdi-filter-variant" />筛选
            </v-btn>
            <v-btn v-hasPermi="['ops:stationList:query']" color="primary" rounded="xl" class="action-btn"
              @click="handleSearch">
              <v-icon start icon="mdi-magnify" />查询
            </v-btn>
            <v-btn variant="outlined" color="success" rounded="xl" class="action-btn" @click="resetFilters">
              <v-icon start icon="mdi-refresh" />重置
            </v-btn>
            <v-btn v-hasPermi="['ops:stationList:add']" color="primary" rounded="xl" class="action-btn ml-auto"
              @click="handleAdd">
              <v-icon start icon="mdi-plus" />新增电站
            </v-btn>
            <v-menu location="bottom end" offset="8">
              <template #activator="{ props }">
                <v-btn v-bind="props" variant="outlined" color="primary" rounded="xl" class="action-btn">
                  <v-icon start icon="mdi-dots-horizontal" />更多
                  <v-icon end icon="mdi-menu-down" />
                </v-btn>
              </template>
              <v-list class="more-menu-list rounded-xl" density="comfortable">
                <v-list-item @click="openColumnDialog">
                  <template #prepend>
                    <v-icon icon="mdi-view-column-outline" color="primary" />
                  </template>
                  <v-list-item-title>筛选列</v-list-item-title>
                </v-list-item>
                <v-list-item v-hasPermi="['ops:stationList:export']" @click="openExportDialog">
                  <template #prepend>
                    <v-icon icon="mdi-download-outline" color="primary" />
                  </template>
                  <v-list-item-title>导出电站</v-list-item-title>
                </v-list-item>
                <v-list-item v-hasPermi="['ops:stationList:exportEfficiency']" @click="openEfficiencyExportDialog">
                  <template #prepend>
                    <v-icon icon="mdi-chart-line" color="primary" />
                  </template>
                  <v-list-item-title>导出电站效率</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>

      <!-- 表格：表头固定，仅 tbody 区域随剩余高度滚动 -->
      <div class="table-wrap">
        <div v-if="displayList.length" class="stations-table-host">
          <v-table fixed-header height="100%" class="gm-table stations-table gm-border rounded-lg"
            density="comfortable">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <v-checkbox :model-value="isAllPageSelected" :indeterminate="isPageIndeterminate" hide-details
                    density="compact" @update:model-value="toggleSelectAllPage" />
                </th>
                <th v-for="column in visibleColumns" :key="column.key" :class="column.headerClass"
                  :style="{ width: column.width + 'px', minWidth: column.width + 'px' }">{{ column.label }}
                </th>
                <th class="text-no-wrap sticky-action-col text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in displayList" :key="rowKey(item, idx)">
                <td class="checkbox-col">
                  <v-checkbox :model-value="isRowSelected(item)" hide-details density="compact"
                    @update:model-value="(v) => toggleRow(item, v)" />
                </td>
                <td v-for="column in visibleColumns" :key="column.key" :class="column.cellClass"
                  :style="{ width: column.width + 'px', minWidth: column.width + 'px' }"
                  :title="getCellTitle(column, item)">
                  <template v-if="column.key === 'name'">
                    <div class="cell-name">{{ item.name || '--' }}</div>
                    <div class="text-caption text-medium-emphasis">ID：{{ item.stationId ?? item.id ?? '--' }}</div>
                  </template>
                  <template v-else-if="column.key === 'region'">
                    {{ formatRegion(item) }}
                  </template>
                  <template v-else-if="column.key === 'stationState'">
                    <v-chip :color="stationStatusMap.getClass(item.stationState)" size="small" variant="tonal" label>
                      {{ stationStatusMap.getText(item.stationState) }}
                    </v-chip>
                  </template>
                  <template v-else-if="column.key === 'meterMode'">
                    {{ formatMeterMode(item) }}
                  </template>
                  <template v-else-if="column.key === 'strategySwitch'">
                    <div class="text-center">
                      <v-icon v-if="strategyOn(item)" color="success" size="22">mdi-check</v-icon>
                      <span v-else class="text-medium-emphasis">—</span>
                    </div>
                  </template>
                  <template v-else>
                    {{ column.render(item) }}
                  </template>
                </td>
                <td class="sticky-action-col text-center">
                  <div class="d-flex justify-center ga-2">
                    <v-menu location="bottom end" offset="8">
                      <template #activator="{ props }">
                        <v-btn v-bind="props" v-hasPermi="['ops:stationList:detail']" variant="text" color="#2655f1"
                          rounded="xl">
                          电站信息
                          <v-icon end icon="mdi-menu-down" />
                        </v-btn>
                      </template>
                      <v-list class="more-menu-list rounded-xl" density="comfortable">
                        <v-list-item @click="handleView(item, 'basic')">
                          <template #prepend>
                            <v-icon icon="mdi-information-outline" color="primary" />
                          </template>
                          <v-list-item-title>基本信息</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="handleView(item, 'device')">
                          <template #prepend>
                            <v-icon icon="mdi-devices" color="primary" />
                          </template>
                          <v-list-item-title>设备接入</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="handleView(item, 'topo')">
                          <template #prepend>
                            <v-icon icon="mdi-graph-outline" color="primary" />
                          </template>
                          <v-list-item-title>电站拓扑</v-list-item-title>
                        </v-list-item>
                        <v-divider class="my-1" />
                        <v-list-item v-if="canToggleOperation(item)" v-hasPermi="['ops:stationList:edit']"
                          @click="handleToggleOperation(item)">
                          <template #prepend>
                            <v-icon :icon="isPaused(item) ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline'"
                              :color="isPaused(item) ? '#00B67A' : '#F5A623'" />
                          </template>
                          <v-list-item-title>{{ toggleOperationLabel(item) }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-hasPermi="['ops:stationList:delete']" class="menu-item--danger"
                          @click="handleDelete(item)">
                          <template #prepend>
                            <v-icon icon="mdi-delete-outline" color="#FF6A46" />
                          </template>
                          <v-list-item-title>删除</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div v-else class="empty-tip empty-tip--fill text-center py-10 text-medium-emphasis">
          {{ emptyHint }}
        </div>
      </div>

      <TablePagination v-model="page" v-model:page-size="pageSize" class="stations-pagination flex-shrink-0"
        :total="total" :has-sizes="true" :has-jump="true" @change="fetchList" />

      <transition name="batch-bar">
        <div v-if="hasSelection" class="batch-float-bar">
          <span class="batch-float-bar__count">已选 <b>{{ selectedIds.size }}</b> 项</span>
          <v-btn v-hasPermi="['ops:stationList:delete']" color="#FF6A46" variant="flat" rounded="pill"
            class="action-btn" @click="handleBatchDelete">
            <v-icon start icon="mdi-delete-outline" />批量删除
          </v-btn>
        </div>
      </transition>
    </v-card>

    <v-dialog v-model="advancedFilterDialogVisible" class="advanced-filter-dialog gm-dialog" max-width="420"
      scrim="rgba(15, 23, 42, 0.24)" transition="slide-x-reverse-transition">
      <v-card class="advanced-filter-card overflow-hidden">
        <div class="advanced-filter-header d-flex align-center px-8 py-6">
          <div class="text-h5"><span style="color: #2655f1">筛选</span><span>条件</span></div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="closeAdvancedFilterDialog" />
        </div>

        <div class="advanced-filter-body px-8 pt-4 pb-2">
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">电站状态</div>
            <v-select v-model="advancedFilterDraft.stationState" :items="statusSelectItems" item-title="title"
              item-value="value" placeholder="全部状态" variant="outlined" density="compact" hide-details clearable
              rounded="xl" class="gm-input" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">投资方</div>
            <v-text-field v-model="advancedFilterDraft.investor" placeholder="请输入投资方" variant="outlined"
              density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">业主方</div>
            <v-text-field v-model="advancedFilterDraft.proprietor" placeholder="请输入业主方" variant="outlined"
              density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">渠道方</div>
            <v-text-field v-model="advancedFilterDraft.channel" placeholder="请输入渠道方" variant="outlined"
              density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">项目编码</div>
            <v-text-field v-model="advancedFilterDraft.projectCode" placeholder="请输入项目编码" variant="outlined"
              density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
        </div>

        <div class="advanced-filter-actions px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4" @click="resetAdvancedFiltersOnly">
            重置筛选
          </v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" @click="applyAdvancedFilters">
            确定
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="columnDialogVisible" max-width="520px" class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5"><span style="color: #2655f1">显示</span><span>列</span></div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="closeColumnDialog" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-caption text-medium-emphasis mb-3">勾选后点击确认生效</div>
          <div class="column-picker-grid">
            <label v-for="column in optionalColumnOptions" :key="column.key" class="column-picker-grid__item">
              <v-checkbox :model-value="columnDraftSet.has(column.key)" hide-details density="compact"
                @update:model-value="toggleColumnDraft(column.key, $event)" />
              <span>{{ column.label }}</span>
            </label>
          </div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="text" color="#2655f1" class="px-6 mr-auto" @click="resetColumnSelection">重置</v-btn>
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="closeColumnDialog">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white"
            @click="applyColumnSelection">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">删除</span><span>确认</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="deleteDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">{{ deleteDialogMessage }}</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="deleteDialogVisible = false">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="deleteLoading"
            @click="doDelete">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="toggleDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">操作</span><span>确认</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="toggleDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">{{ toggleDialogMessage }}</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="toggleDialogVisible = false">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="toggleLoading"
            @click="doToggleOperation">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="exportDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">系统</span><span>提示</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="exportDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">确定要导出所有电站吗?</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="exportDialogVisible = false">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="exportLoading"
            @click="handleExportStations">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="efficiencyDialogVisible" max-width="520px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">请选择日期范围</div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)"
            @click="efficiencyDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="efficiency-tabs mb-5">
            <v-tabs v-model="efficiencyExportType" color="#2655f1" align-tabs="start" density="comfortable">
              <v-tab value="month">按月</v-tab>
              <v-tab value="week">按周</v-tab>
            </v-tabs>
          </div>

          <div v-if="efficiencyExportType === 'month'" class="efficiency-picker-row efficiency-picker-row--month">
            <MonthSelect v-model="efficiencyMonthStart" width="100%" class="efficiency-picker-field" />
            <span class="efficiency-range-separator">-</span>
            <MonthSelect v-model="efficiencyMonthEnd" width="100%" class="efficiency-picker-field" />
          </div>

          <div v-else class="efficiency-picker-row efficiency-picker-row--week">
            <v-menu v-model="efficiencyWeekMenuVisible" :close-on-content-click="false" location="bottom" offset="8">
              <template #activator="{ props: menuProps }">
                <v-text-field v-bind="menuProps" :model-value="efficiencyWeekDateDisplay" placeholder="请选择日期"
                  variant="outlined" density="compact" hide-details rounded="xl" readonly
                  class="efficiency-picker-field" append-inner-icon="mdi-calendar-month-outline" />
              </template>
              <v-date-picker :model-value="efficiencyWeekDateValue" color="primary"
                @update:model-value="handleEfficiencyWeekDateChange" />
            </v-menu>
            <div class="efficiency-week-preview">{{ weekRangeDisplay }}</div>
          </div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="efficiencyDialogVisible = false">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white"
            :loading="efficiencyExportLoading" @click="handleExportEfficiency">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { saveAs } from 'file-saver'
import * as stationApi from '@/api/device/station'
import { listEBlockModel } from '@/api/device/model'
import { regionData } from '@/utils/regionData'
import TablePagination from '@/components/TablePagination/index.vue'
import MonthSelect from '@/components/MonthSelect/index.vue'
import { createStatusMap } from '@/utils'
import { eui } from 'ems-ui'
import {
  statusSelectItems,
  STATION_STATUS_VALUE,
} from './stationStatusFilter'

const router = useRouter()

dayjs.extend(isoWeek)

const pendingStatusLabel = null

const loading = ref(false)
const stationList = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(10)

const queryProvince = ref('')
const queryEBlockModel = ref('')
const searchKeyword = ref('')
const searchInput = ref('')

const filterApplied = reactive({
  customer: '',
  operatingUnit: '',
  devopsUnit: '',
  uuid: '',
  stationState: '',
})

const eBlockModelOptions = ref([])

const eBlockModelFilterItems = computed(() => {
  return [{ title: '全部', value: '' }, ...eBlockModelOptions.value]
})


const stationStatusMap = createStatusMap({
  0: { text: '已投运', class: 'success' },
  1: { text: '未投运', class: 'warning' },
  2: { text: '暂停运营', class: 'danger' },
})

const selectedIds = ref(new Set())

const provinceItems = computed(() => {
  const list = [{ title: '全部区域', value: '' }]
  for (const p of Object.keys(regionData)) {
    list.push({ title: p, value: p })
  }
  return list
})

const advancedFilterDialogVisible = ref(false)
const advancedFilterDraft = reactive({
  investor: '',
  proprietor: '',
  channel: '',
  projectCode: '',
  stationState: '',
})

function syncAdvancedFilterDraft() {
  advancedFilterDraft.investor = filterApplied.customer
  advancedFilterDraft.proprietor = filterApplied.operatingUnit
  advancedFilterDraft.channel = filterApplied.devopsUnit
  advancedFilterDraft.projectCode = filterApplied.uuid
  advancedFilterDraft.stationState = filterApplied.stationState
}

const hasAdvancedFilters = computed(() => {
  return Boolean(
    filterApplied.customer ||
    filterApplied.operatingUnit ||
    filterApplied.devopsUnit ||
    filterApplied.uuid
  ) || (filterApplied.stationState !== '' && filterApplied.stationState != null)
})

async function loadEBlockModelOptions() {
  try {
    const res = await listEBlockModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    const options = Array.isArray(list)
      ? list
        .map((item) => {
          const productModel = String(item?.productModel ?? '').trim()
          if (!productModel) return null
          return {
            title: productModel,
            value: productModel,
          }
        })
        .filter(Boolean)
      : []

    const seen = new Set()
    eBlockModelOptions.value = options.filter((item) => {
      if (seen.has(item.value)) return false
      seen.add(item.value)
      return true
    })
  } catch (e) {
    console.warn(e)
    eBlockModelOptions.value = []
  }
}

function openAdvancedFilterDialog() {
  syncAdvancedFilterDraft()
  advancedFilterDialogVisible.value = true
}

function closeAdvancedFilterDialog() {
  advancedFilterDialogVisible.value = false
}

const columnDialogVisible = ref(false)

function firstNonEmpty(values = []) {
  const hit = values.find((value) => value != null && String(value).trim() !== '')
  return hit == null ? '--' : String(hit)
}

function formatMeterMode(item) {
  const raw = item?.meterModel ?? item?.meterMode
  if (raw == null || raw === '') return '--'
  const n = Number(raw)
  if (n === 0) return '虚拟表'
  if (n === 1) return '实体表'
  return String(raw)
}

function formatOperationModel(item) {
  const raw = item?.operatingModel ?? item?.operationModel
  if (raw == null || raw === '') return '--'
  if (raw === 'self') return '自运营模式'
  if (raw === 'emc') return '合同能源管理'
  const n = Number(raw)
  if (n === 1) return '自运营模式'
  if (n === 2) return '合同能源管理'
  return String(raw)
}

function formatElectricityCategory(item) {
  const raw = item?.classification ?? item?.electricityCategory
  if (raw == null || raw === '') return '--'
  if (raw === 'industrial') return '一般工业用电'
  if (raw === 'large_industrial') return '大工业用电'
  const n = Number(raw)
  if (n === 0) return '大工业用电'
  if (n === 1) return '一般工业用电'
  return String(raw)
}

const allColumnOptions = Object.freeze([
  { key: 'name', label: '电站名称', width: 260, render: (item) => item.name || '--' },
  { key: 'region', label: '所属城市', width: 160, render: (item) => formatRegion(item) },
  { key: 'stationState', label: '电站状态', width: 110, render: (item) => stationStatusMap.getText(item.stationState) },
  { key: 'commissioningTime', label: '投运日期', width: 120, render: (item) => fmtDate(item.commossioningTime ?? item.verificationDate) },
  { key: 'powerCapacity', label: '额定功率/容量', width: 180, render: (item) => formatPowerCapacity(item) },
  { key: 'strategySwitch', label: '策略控制', width: 100, headerClass: 'text-center', cellClass: 'text-center', render: () => '--' },
  { key: 'voltage', label: '电压等级（kV）', width: 130, render: (item) => item.voltage ?? '--' },
  { key: 'eBlockModel', label: 'eBlock型号', width: 180, render: (item) => formatEBlockModel(item) },
  { key: 'investor', label: '投资方', width: 140, render: (item) => firstNonEmpty([item.customer, item.customerName]) },
  { key: 'proprietor', label: '业主方', width: 140, render: (item) => firstNonEmpty([item.operatingUnit]) },
  { key: 'channel', label: '渠道方', width: 140, render: (item) => firstNonEmpty([item.devopsUnit]) },
  { key: 'projectCode', label: '项目编码', width: 160, render: (item) => firstNonEmpty([item.uuid]) },
  { key: 'operationModel', label: '运营模式', width: 140, render: (item) => formatOperationModel(item) },
  { key: 'electricityCategory', label: '用电类别', width: 140, render: (item) => formatElectricityCategory(item) },
])

const defaultVisibleColumnKeys = [
  'name',
  'region',
  'stationState',
  'commissioningTime',
  'powerCapacity',
  'strategySwitch',
  'voltage',
  'eBlockModel',
]

const COLUMN_STORAGE_KEY = 'ops:stationList:visibleColumns'

function loadStoredColumnKeys() {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY)
    if (!raw) return [...defaultVisibleColumnKeys]
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return [...defaultVisibleColumnKeys]
    const validKeys = new Set(allColumnOptions.map((c) => c.key))
    const filtered = parsed.filter((key) => validKeys.has(key))
    return filtered.length ? filtered : [...defaultVisibleColumnKeys]
  } catch (e) {
    return [...defaultVisibleColumnKeys]
  }
}

function saveColumnKeys(keys) {
  try {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(keys))
  } catch (e) {
    console.warn(e)
  }
}

const selectedColumnKeys = ref(loadStoredColumnKeys())
const columnDraftSet = ref(new Set(selectedColumnKeys.value))

const visibleColumns = computed(() => {
  const selected = new Set(selectedColumnKeys.value)
  return allColumnOptions.filter((column) => selected.has(column.key))
})

const optionalColumnOptions = computed(() => allColumnOptions)

watch(columnDialogVisible, (open) => {
  if (open) {
    columnDraftSet.value = new Set(selectedColumnKeys.value)
  }
})

function toggleColumnDraft(key, checked) {
  const next = new Set(columnDraftSet.value)
  if (checked) next.add(key)
  else next.delete(key)
  columnDraftSet.value = next
}

function openColumnDialog() {
  columnDraftSet.value = new Set(selectedColumnKeys.value)
  columnDialogVisible.value = true
}

function closeColumnDialog() {
  columnDraftSet.value = new Set(selectedColumnKeys.value)
  columnDialogVisible.value = false
}

function resetColumnSelection() {
  columnDraftSet.value = new Set(defaultVisibleColumnKeys)
}

function applyColumnSelection() {
  const nextKeys = allColumnOptions
    .filter((column) => columnDraftSet.value.has(column.key))
    .map((column) => column.key)

  if (!nextKeys.length) {
    eui.showToast('请至少保留一列', 'error')
    return
  }

  selectedColumnKeys.value = nextKeys
  saveColumnKeys(nextKeys)
  columnDialogVisible.value = false
}

const displayList = computed(() => stationList.value)

function getCellTitle(column, item) {
  switch (column.key) {
    case 'name':
      return `${item.name || '--'}  ID：${item.stationId ?? item.id ?? '--'}`
    case 'region':
      return formatRegion(item)
    case 'stationState':
      return stationStatusMap.getText(item.stationState) || ''
    case 'strategySwitch':
      return ''
    default:
      return column.render ? column.render(item) : ''
  }
}

const emptyHint = computed(() => {
  if (
    searchKeyword.value ||
    queryProvince.value ||
    queryEBlockModel.value ||
    filterApplied.customer ||
    filterApplied.operatingUnit ||
    filterApplied.devopsUnit ||
    filterApplied.uuid ||
    filterApplied.stationState
  ) {
    return filterApplied.stationState === pendingStatusLabel
      ? '暂无待审核的建站申请'
      : '未找到匹配的电站'
  }
  return '暂无电站数据'
})

function rowKey(item, idx) {
  return item.stationId ?? item.id ?? item.stationUUID ?? `row-${idx}`
}

function stationIdOf(item) {
  return item.stationId ?? item.id
}

function fmtDate(v) {
  if (v == null || v === '') return '--'
  const d = dayjs(v)
  return d.isValid() ? d.format('YYYY-MM-DD') : String(v).slice(0, 10) || '--'
}

function formatEfficiencyDate(value) {
  if (!value) return ''
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD') : ''
}

function handleEfficiencyWeekDateChange(value) {
  if (!value) {
    efficiencyWeekDate.value = ''
    efficiencyWeekMenuVisible.value = false
    return
  }
  const date = dayjs(value)
  if (!date.isValid()) return
  efficiencyWeekDate.value = date.format('YYYY-MM-DD')
  efficiencyWeekMenuVisible.value = false
}

function formatRegion(item) {
  const a = [item.province, item.city, item.district].filter(Boolean)
  return a.length ? a.join('') : '--'
}

function formatPowerCapacity(item) {
  const p = item.maxPower
  const c = item.capacity
  if (p == null && c == null) return '--'
  const pk = p != null ? `${Number(p)}kW` : '--'
  const ck = c != null ? `${Number(c)}kWh` : '--'
  return `${pk}/${ck}`
}

function formatEBlockModel(item) {
  const raw = item.eBlockModel ?? item.eblockModel
  if (raw == null || raw === '') return '--'
  const s = String(raw)
  const name = s.split(/[,\s]+/).filter(Boolean).join(', ')
  return name || '--'
}

function strategyOn(item) {
  const v = item.strategySwitch
  if (v === true || v === 1 || v === '1') return true
  if (v === false || v === 0 || v === '0') return false
  if (typeof v === 'string') return ['是', 'Y', 'y', 'true', '开启'].includes(v)
  return Boolean(v)
}

const pageIds = computed(() => displayList.value.map((r) => stationIdOf(r)).filter((id) => id != null))

const isAllPageSelected = computed(() => {
  const ids = pageIds.value
  if (!ids.length) return false
  return ids.every((id) => selectedIds.value.has(id))
})

const isPageIndeterminate = computed(() => {
  const ids = pageIds.value
  if (!ids.length) return false
  const n = ids.filter((id) => selectedIds.value.has(id)).length
  return n > 0 && n < ids.length
})

const hasSelection = computed(() => selectedIds.value.size > 0)

function isRowSelected(item) {
  const id = stationIdOf(item)
  return id != null && selectedIds.value.has(id)
}

function toggleRow(item, on) {
  const id = stationIdOf(item)
  if (id == null) return
  const next = new Set(selectedIds.value)
  if (on) next.add(id)
  else next.delete(id)
  selectedIds.value = next
}

function toggleSelectAllPage(on) {
  const next = new Set(selectedIds.value)
  for (const id of pageIds.value) {
    if (id == null) continue
    if (on) next.add(id)
    else next.delete(id)
  }
  selectedIds.value = next
}

function handleSearch() {
  searchKeyword.value = (searchInput.value || '').trim()
  filterApplied.customer = (filterApplied.customer || '').trim()
  filterApplied.operatingUnit = (filterApplied.operatingUnit || '').trim()
  filterApplied.devopsUnit = (filterApplied.devopsUnit || '').trim()
  filterApplied.uuid = (filterApplied.uuid || '').trim()
  page.value = 1
  fetchList()
}

function applyAdvancedFilters() {
  filterApplied.customer = (advancedFilterDraft.investor || '').trim()
  filterApplied.operatingUnit = (advancedFilterDraft.proprietor || '').trim()
  filterApplied.devopsUnit = (advancedFilterDraft.channel || '').trim()
  filterApplied.uuid = (advancedFilterDraft.projectCode || '').trim()
  filterApplied.stationState = advancedFilterDraft.stationState ?? ''
  handleSearch()
  closeAdvancedFilterDialog()
}

function resetAdvancedFiltersOnly() {
  advancedFilterDraft.investor = ''
  advancedFilterDraft.proprietor = ''
  advancedFilterDraft.channel = ''
  advancedFilterDraft.projectCode = ''
  advancedFilterDraft.stationState = ''
}

function resetFilters() {
  queryProvince.value = ''
  queryEBlockModel.value = ''
  filterApplied.customer = ''
  filterApplied.operatingUnit = ''
  filterApplied.devopsUnit = ''
  filterApplied.uuid = ''
  filterApplied.stationState = ''
  searchInput.value = ''
  searchKeyword.value = ''
  page.value = 1
  fetchList()
}

watch(queryProvince, () => {
  page.value = 1
  fetchList()
})

watch(queryEBlockModel, () => {
  page.value = 1
  fetchList()
})

onMounted(() => {
  loadEBlockModelOptions()
  fetchList()
})

function buildListParams() {
  const params = {
    pageNum: page.value,
    pageSize: pageSize.value,
  }
  if (searchKeyword.value) params.name = searchKeyword.value
  if (queryProvince.value) params.province = queryProvince.value
  if (queryEBlockModel.value) params.eBlockModel = queryEBlockModel.value
  if (filterApplied.customer) params.customer = filterApplied.customer
  if (filterApplied.operatingUnit) params.operatingUnit = filterApplied.operatingUnit
  if (filterApplied.devopsUnit) params.devopsUnit = filterApplied.devopsUnit
  if (filterApplied.uuid) params.uuid = filterApplied.uuid
  if (filterApplied.stationState !== '' && filterApplied.stationState != null) {
    params.stationState = filterApplied.stationState
  }
  return params
}

async function fetchList() {
  selectedIds.value = new Set()
  loading.value = true
  const params = buildListParams()

  try {
    const res = await stationApi.list(params)
    if (res && (res.code === 200 || res.code === undefined)) {
      stationList.value = res.rows || []
      total.value = res.total ?? 0
    }
  } catch (e) {
    console.warn(e)
  } finally {
    loading.value = false
  }
}

function handleAdd() {
  router.push({ path: '/it-operations/station/add' })
}

function handleView(item, step = 'basic') {
  const id = stationIdOf(item)
  if (id == null) return
  router.push({
    path: '/it-operations/station/add',
    query: { stationId: String(id), step },
  })
}

const deleteDialogVisible = ref(false)
const deleteLoading = ref(false)
const deleteTarget = ref(null)
const exportDialogVisible = ref(false)
const exportLoading = ref(false)
const efficiencyDialogVisible = ref(false)
const efficiencyExportLoading = ref(false)
const efficiencyExportType = ref('month')
const efficiencyWeekMenuVisible = ref(false)
const efficiencyMonthStart = ref(dayjs().format('YYYY-MM'))
const efficiencyMonthEnd = ref(dayjs().add(11, 'month').format('YYYY-MM'))
const efficiencyWeekDate = ref(dayjs().format('YYYY-MM-DD'))
const efficiencyWeekDateValue = computed(() => efficiencyWeekDate.value || null)
const efficiencyWeekDateDisplay = computed(() => formatEfficiencyDate(efficiencyWeekDate.value))
const weekRangeDisplay = computed(() => {
  if (!efficiencyWeekDate.value) return '--'
  const target = dayjs(efficiencyWeekDate.value)
  if (!target.isValid()) return '--'
  return `${target.startOf('isoWeek').format('YYYY-MM-DD')} - ${target.endOf('isoWeek').format('YYYY-MM-DD')} 第${target.isoWeek()}周`
})
const deleteDialogMessage = computed(() => {
  const t = deleteTarget.value
  if (!t) return ''
  if (t.type === 'batch') return `确定删除已选中的 ${t.ids.length} 个电站吗？`
  return `确定删除「${t.name}」吗？`
})

function handleDelete(item) {
  const id = stationIdOf(item)
  if (id == null) return
  deleteTarget.value = { type: 'single', id, name: item.name || String(id) }
  deleteDialogVisible.value = true
}

const toggleDialogVisible = ref(false)
const toggleLoading = ref(false)
const toggleTarget = ref(null)

function normalizeStationState(item) {
  const raw = item?.stationState
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function isPaused(item) {
  return normalizeStationState(item) === STATION_STATUS_VALUE.STOPPED
}

function isCommissioned(item) {
  return normalizeStationState(item) === STATION_STATUS_VALUE.PUT_INTO
}

function canToggleOperation(item) {
  return isPaused(item) || isCommissioned(item)
}

function toggleOperationLabel(item) {
  if (isCommissioned(item)) return '暂停运营'
  if (isPaused(item)) return '电站投运'
  return ''
}

const toggleDialogMessage = computed(() => {
  const t = toggleTarget.value
  if (!t) return ''
  return `确定将「${t.name}」${t.actionText}吗？`
})

function handleToggleOperation(item) {
  const id = stationIdOf(item)
  if (id == null || !canToggleOperation(item)) return
  const nextState = isCommissioned(item)
    ? STATION_STATUS_VALUE.STOPPED
    : STATION_STATUS_VALUE.PUT_INTO
  const actionText = nextState === STATION_STATUS_VALUE.STOPPED ? '暂停运营' : '电站投运'
  toggleTarget.value = {
    id,
    name: item.name || String(id),
    nextState,
    actionText,
  }
  toggleDialogVisible.value = true
}

async function doToggleOperation() {
  const t = toggleTarget.value
  if (!t) return
  toggleLoading.value = true
  try {
    const res = await stationApi.detail(t.id)
    const station = res?.data?.station ?? res?.station
    const strategyConfig = res?.data?.strategyConfig ?? res?.strategyConfig ?? null
    if (!station || typeof station !== 'object') {
      eui.showToast('获取电站详情失败', 'error')
      return
    }
    const payload = {
      station: {
        ...station,
        stationId: station.stationId ?? station.id ?? t.id,
        stationState: t.nextState,
      },
    }
    if (strategyConfig) payload.strategyConfig = strategyConfig
    await stationApi.update(payload)
    eui.showToast(`${t.actionText}成功`, 'success')
    toggleDialogVisible.value = false
    toggleTarget.value = null
    await fetchList()
  } catch (e) {
    console.warn(e)
  } finally {
    toggleLoading.value = false
  }
}

function handleBatchDelete() {
  const ids = [...selectedIds.value]
  if (!ids.length) return
  deleteTarget.value = { type: 'batch', ids }
  deleteDialogVisible.value = true
}

function openExportDialog() {
  exportDialogVisible.value = true
}

function openEfficiencyExportDialog() {
  efficiencyDialogVisible.value = true
}

function getEfficiencyExportParams() {
  if (efficiencyExportType.value === 'month') {
    if (!efficiencyMonthStart.value || !efficiencyMonthEnd.value) {
      eui.showToast('请选择日期范围', 'error')
      return null
    }

    const start = dayjs(`${efficiencyMonthStart.value}-01`)
    const end = dayjs(`${efficiencyMonthEnd.value}-01`)
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
      eui.showToast('请选择正确的日期范围', 'error')
      return null
    }

    return {
      startTime: start.startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endTime: end.endOf('month').format('YYYY-MM-DD HH:mm:ss'),
      type: 'month',
    }
  }

  if (!efficiencyWeekDate.value) {
    eui.showToast('请选择日期范围', 'error')
    return null
  }

  const target = dayjs(efficiencyWeekDate.value)
  if (!target.isValid()) {
    eui.showToast('请选择正确的日期范围', 'error')
    return null
  }

  return {
    startTime: target.startOf('isoWeek').format('YYYY-MM-DD HH:mm:ss'),
    endTime: target.endOf('isoWeek').format('YYYY-MM-DD HH:mm:ss'),
    type: 'week',
  }
}

async function handleExportEfficiency() {
  const params = getEfficiencyExportParams()
  if (!params) return

  efficiencyExportLoading.value = true
  try {
    const blob = await stationApi.exportStationEfficiency(params)
    saveAs(blob, `电站效率_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`)
    efficiencyDialogVisible.value = false
    eui.showToast('导出成功', 'success')
  } catch (e) {
    console.warn(e)
  } finally {
    efficiencyExportLoading.value = false
  }
}

async function handleExportStations() {
  exportLoading.value = true
  try {
    const blob = await stationApi.exportList({
      stationState: '',
      name: '',
    })
    saveAs(blob, `电站列表_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`)
    exportDialogVisible.value = false
    eui.showToast('导出成功', 'success')
  } catch (e) {
    console.warn(e)
  } finally {
    exportLoading.value = false
  }
}

async function doDelete() {
  const t = deleteTarget.value
  if (!t) return
  deleteLoading.value = true
  try {
    if (t.type === 'batch') {
      for (const id of t.ids) {
        await stationApi.del(String(id))
      }
      selectedIds.value = new Set()
    } else {
      await stationApi.del(String(t.id))
      const next = new Set(selectedIds.value)
      next.delete(t.id)
      selectedIds.value = next
    }
    eui.showToast('删除成功', 'success')
    deleteDialogVisible.value = false
    await fetchList()
  } catch (e) {
    console.warn(e)
  } finally {
    deleteLoading.value = false
  }
}

</script>

<style scoped lang="scss">
.stations-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  box-sizing: border-box;
}

.toolbar-top {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar-main,
.toolbar-actions {
  min-width: 0;
}

.toolbar-actions {
  justify-content: flex-start;
  row-gap: 12px;
}

.action-btn {
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .search-label {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    text-align: right;
    min-width: 64px;
  }

  .search-input {
    width: 220px;
  }

  .search-input--select {
    width: 180px;
  }
}

.search-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  row-gap: 12px;
  flex: 1;
}

.action-btn--active {
  color: #fff !important;
}

.stations-card {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.more-menu-list {
  min-width: 180px;
  padding: 6px;
}

.more-menu-list :deep(.v-list-item) {
  border-radius: 10px;
}

.more-menu-list :deep(.v-list-item:hover) {
  background: rgba(38, 85, 241, 0.04);
}

.more-menu-list :deep(.menu-item--danger .v-list-item-title) {
  color: #FF6A46;
}

.more-menu-list :deep(.menu-item--danger:hover) {
  background: rgba(255, 106, 70, 0.06);
}

.column-picker-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
  max-height: 360px;
  overflow: auto;
}

.column-picker-grid__item {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 8px;
  cursor: pointer;
  border-radius: 10px;
}

.column-picker-grid__item:hover {
  background: rgba(38, 85, 241, 0.04);
}

.table-wrap {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.stations-table-host {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.stations-table {
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
}

/* 表体纵向滚动在 .v-table__wrapper；同时保留横向滚动（Vuetify fixed-height 仅设 overflow-y 时补全为 auto） */
.stations-table :deep(.v-table__wrapper) {
  overflow: auto;
}

/* 固定列宽，避免翻页时列跳动 */
.stations-table :deep(.v-table__wrapper > table) {
  width: 100%;
  min-width: max-content;
  table-layout: fixed;
}

/* 单元格文本溢出省略，防止内容撑破固定列宽 */
.stations-table :deep(tbody td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stations-table :deep(tbody td.text-center) {
  white-space: nowrap;
}

.stations-table :deep(tbody td .cell-name),
.stations-table :deep(tbody td .text-caption) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stations-table :deep(thead th) {
  background: rgba(255, 255, 255, 0.96) !important;
}

/* 操作列固定在可视区域右侧，横向滚动时左侧列从下方滑过 */
.stations-table :deep(th.sticky-action-col),
.stations-table :deep(td.sticky-action-col) {
  position: sticky;
  right: 0;
  white-space: nowrap;
  width: 160px;
  min-width: 160px;
  box-sizing: border-box;
  box-shadow: -8px 0 12px rgba(15, 23, 42, 0.06);
}

.stations-table :deep(th.sticky-action-col) {
  z-index: 6;
  background-color: #fff !important;
}

.stations-table :deep(td.sticky-action-col) {
  z-index: 1;
  background-color: #fff;
}

/* 悬浮时保持实底 + 淡蓝，与业务配置一致的视觉层次 */
.stations-table :deep(tbody tr:hover td.sticky-action-col) {
  background-color: #fff;
  background-image: linear-gradient(90deg, rgba(38, 85, 241, 0.06) 0%, rgba(85, 7, 253, 0.06) 100%);
}

.empty-tip--fill {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-col {
  width: 48px;
  vertical-align: middle;
}

.cell-name {
  max-width: 280px;
  font-weight: 500;
}

.stations-pagination {
  :deep(.table-pagination) {
    padding-left: 0;
    padding-right: 0;
  }
}

.batch-float-bar {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 8px 8px 24px;
  background: #fff;
  border-radius: 999px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
}

.batch-float-bar__count {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
  white-space: nowrap;

  b {
    color: #2655f1;
    margin: 0 2px;
  }
}

.batch-bar-enter-active,
.batch-bar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.batch-bar-enter-from,
.batch-bar-leave-to {
  opacity: 0;
  transform: translate(-50%, 16px);
}

.status-quick-label {
  flex-shrink: 0;
  line-height: 32px;
}

.status-quick-row {
  :deep(.v-tabs) {
    min-height: 40px;
  }

  :deep(.v-tab) {
    min-height: 40px;
    text-transform: none;
    letter-spacing: 0;
  }
}

.status-chip-badge {
  margin-left: 4px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  border-radius: 9px;
  background: rgba(47, 89, 235, 0.12);
  color: #2f59eb;
}


.advanced-filter-card {
  height: 100vh;
  max-height: 100vh;
  border-radius: 30px 0 0 30px !important;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #fff;
}

.advanced-filter-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  flex-shrink: 0;

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.advanced-filter-body {
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

.advanced-filter-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.advanced-filter-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.advanced-filter-actions {
  display: flex;
  flex-shrink: 0;
}

.advanced-filter-dialog :deep(.v-overlay__content) {
  margin: 0 !important;
  left: auto !important;
  right: 0 !important;
  width: min(420px, 100vw) !important;
  max-width: min(420px, 100vw) !important;
  height: 100% !important;
  max-height: 100% !important;
}


.dialog-card {
  border-radius: 30px !important;
  background: #fff;
}

.gm-dialog-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  position: relative;

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.gm-btn-primary,
.gm-btn-secondary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
}

.efficiency-picker-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.efficiency-picker-row--month .efficiency-picker-field {
  flex: 1 1 0;
  min-width: 0;
}

.efficiency-picker-row--week {
  flex-direction: column;
  align-items: stretch;
}

.efficiency-picker-field {
  width: 100%;
}

.efficiency-range-separator {
  color: rgba(0, 0, 0, 0.7);
  font-size: 18px;
  line-height: 1;
}

.efficiency-week-preview {
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
  line-height: 22px;
}

:deep(.v-picker__header-wrapper) {
  display: none !important;
}

/* ============ 小屏 1366x768 兼容适配 ============ */
@media (max-width: 1440px) {
  .toolbar-main {
    gap: 12px !important;
    row-gap: 10px !important;
  }

  .search-item {
    gap: 8px;

    .search-label {
      min-width: auto;
      font-size: 13px;
    }

    .search-input {
      width: 180px;
    }

    .search-input--select {
      width: 140px;
    }
  }

  .search-actions {
    gap: 8px !important;
    row-gap: 8px !important;
  }

  .action-btn {
    padding: 0 12px !important;
    font-size: 13px !important;
    letter-spacing: 0 !important;
    min-width: 0 !important;
  }

  /* 操作列稍微收窄，为主内容留出更多横向空间 */
  .stations-table :deep(th.sticky-action-col),
  .stations-table :deep(td.sticky-action-col) {
    width: 140px;
    min-width: 140px;
  }
}

@media (max-width: 1366px) {
  .toolbar-main {
    gap: 10px !important;
    row-gap: 8px !important;
  }

  .search-item {
    gap: 6px;

    .search-label {
      font-size: 12px;
    }

    .search-input {
      width: 160px;
    }

    .search-input--select {
      width: 128px;
    }
  }

  .search-actions {
    gap: 6px !important;
    row-gap: 8px !important;
  }

  .action-btn {
    padding: 0 10px !important;
    font-size: 12px !important;
    height: 32px !important;
  }

  /* 让按钮组的图标间距更紧凑 */
  .action-btn :deep(.v-icon) {
    font-size: 16px !important;
  }

  /* 减小卡片内边距，为表格留更多空间 */
  .stations-card {
    padding: 12px !important;
  }

  /* 操作列进一步收窄 */
  .stations-table :deep(th.sticky-action-col),
  .stations-table :deep(td.sticky-action-col) {
    width: 128px;
    min-width: 128px;
  }

  /* 减少表头/单元格默认横向内边距 */
  .stations-table :deep(thead th),
  .stations-table :deep(tbody td) {
    padding-inline: 12px !important;
  }

  .cell-name {
    max-width: 220px;
  }
}

@media (max-height: 800px) {

  /* 小高度屏（如 1366x768）压缩顶部工具区，尽量为表格保留纵向空间 */
  .toolbar-top {
    margin-bottom: 12px !important;
  }

  .stations-pagination {
    :deep(.table-pagination) {
      padding-top: 8px;
      padding-bottom: 4px;
    }
  }

  .batch-float-bar {
    bottom: 16px;
    padding: 6px 6px 6px 20px;
  }
}
</style>
